import { useState } from "react";
import type { DocumentIntelligence, Field, Theme, FontPairing, Finding, DerivedField, RecommendedAction } from "@structurify/shared-types";
import { MonoLabel } from "../components/primitives/index.ts";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  intel: DocumentIntelligence;
  fields: Field[];
  onJumpToField: (id: string) => void;
  onClose: () => void;
  mobile: boolean;
}

type Tab = "summary" | "findings" | "derived" | "actions";

export function InsightsPanel({ theme, fonts, intel, onJumpToField, onClose, mobile }: Props) {
  const [tab, setTab] = useState<Tab>("summary");

  return (
    <div
      style={{
        width: mobile ? "100%" : 360,
        flexShrink: 0,
        borderLeft: mobile ? "none" : `1px solid ${theme.rule}`,
        borderTop: mobile ? `1px solid ${theme.rule}` : "none",
        background: theme.surface,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "14px 18px",
          borderBottom: `1px solid ${theme.rule}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <MonoLabel theme={theme} fonts={fonts} size={9}>INTELLIGENCE</MonoLabel>
        <div style={{ flex: 1 }} />
        <RiskGauge intel={intel} theme={theme} fonts={fonts} />
        <button
          onClick={onClose}
          style={{ background: "transparent", border: "none", color: theme.inkMute, fontSize: 16, cursor: "pointer", padding: "0 4px" }}
        >
          ×
        </button>
      </div>

      <div style={{ display: "flex", borderBottom: `1px solid ${theme.rule}`, flexShrink: 0 }}>
        {(
          [
            ["summary", "Summary"],
            ["findings", `Findings · ${intel.findings.length}`],
            ["derived", `Derived · ${intel.derived.length}`],
            ["actions", "Actions"],
          ] as [Tab, string][]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            style={{
              flex: 1,
              padding: "10px 6px",
              background: "transparent",
              border: "none",
              borderBottom: `2px solid ${tab === k ? theme.ink : "transparent"}`,
              fontFamily: fonts.sans,
              fontSize: 11.5,
              fontWeight: tab === k ? 500 : 450,
              color: tab === k ? theme.ink : theme.inkMute,
              cursor: "pointer",
              letterSpacing: "-0.005em",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {tab === "summary" && <InsightSummary intel={intel} theme={theme} fonts={fonts} />}
        {tab === "findings" && (
          <InsightFindings intel={intel} theme={theme} fonts={fonts} onJumpToField={onJumpToField} />
        )}
        {tab === "derived" && <InsightDerived intel={intel} theme={theme} fonts={fonts} />}
        {tab === "actions" && <InsightActions intel={intel} theme={theme} fonts={fonts} />}
      </div>
    </div>
  );
}

function RiskGauge({ intel, theme, fonts }: { intel: DocumentIntelligence; theme: Theme; fonts: FontPairing }) {
  const score = intel.risk.score;
  const color = score < 35 ? theme.inkSoft : score < 65 ? theme.accent : theme.confidenceLow;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.06em" }}>RISK</span>
      <div style={{ width: 42, height: 4, background: theme.rule, position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${score}%`, background: color }} />
      </div>
      <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.ink, fontWeight: 500, minWidth: 18, textAlign: "right" }}>
        {score}
      </span>
    </div>
  );
}

function RiskBar({ score, theme, fonts }: { score: number; theme: Theme; fonts: FontPairing }) {
  const color = score < 35 ? theme.inkSoft : score < 65 ? theme.accent : theme.confidenceLow;
  return (
    <div style={{ position: "relative", height: 6, background: theme.rule }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${score}%`, background: color }} />
      <div style={{ position: "absolute", left: "35%", top: -2, bottom: -2, width: 1, background: theme.ruleStrong, opacity: 0.4 }} />
      <div style={{ position: "absolute", left: "65%", top: -2, bottom: -2, width: 1, background: theme.ruleStrong, opacity: 0.4 }} />
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: fonts.mono,
          fontSize: 9,
          color: theme.inkMute,
          letterSpacing: "0.06em",
        }}
      >
        <span>LOW</span>
        <span>MOD</span>
        <span>HIGH</span>
      </div>
    </div>
  );
}

function InsightSummary({ intel, theme, fonts }: { intel: DocumentIntelligence; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ padding: "20px 18px" }}>
      <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 8 }}>DOCUMENT</MonoLabel>
      <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, fontWeight: 500, marginBottom: 4 }}>
        {intel.summary.document_type}
      </div>
      <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, lineHeight: 1.5 }}>{intel.summary.intent}</div>

      <div style={{ height: 1, background: theme.rule, margin: "20px 0" }} />

      <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 8 }}>ANALYSIS</MonoLabel>
      <div style={{ fontFamily: fonts.sans, fontSize: 14, color: theme.ink, lineHeight: 1.55, fontWeight: 450 }}>
        {intel.summary.one_line}
      </div>

      <div style={{ height: 1, background: theme.rule, margin: "20px 0" }} />

      <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 10 }}>
        RISK · {intel.risk.band.toUpperCase()}
      </MonoLabel>
      <RiskBar score={intel.risk.score} theme={theme} fonts={fonts} />
      <div style={{ fontFamily: fonts.sans, fontSize: 12.5, color: theme.inkSoft, lineHeight: 1.5, marginTop: 10 }}>
        {intel.risk.rationale}
      </div>

      <div style={{ height: 1, background: theme.rule, margin: "20px 0" }} />

      <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 10 }}>AT A GLANCE</MonoLabel>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: theme.rule, border: `1px solid ${theme.rule}` }}>
        {intel.derived.map((d) => (
          <div key={d.id} style={{ background: theme.surface, padding: "10px 12px" }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.06em" }}>
              {d.label.toUpperCase()}
            </div>
            <div style={{ fontFamily: fonts.sans, fontSize: 16, color: theme.ink, fontWeight: 500, marginTop: 4, letterSpacing: "-0.01em" }}>
              {d.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InsightFindings({
  intel,
  theme,
  fonts,
  onJumpToField,
}: {
  intel: DocumentIntelligence;
  theme: Theme;
  fonts: FontPairing;
  onJumpToField: (id: string) => void;
}) {
  return (
    <div style={{ padding: "8px 0" }}>
      {intel.findings.map((f, i) => (
        <FindingCard key={i} finding={f} theme={theme} fonts={fonts} onJumpToField={onJumpToField} />
      ))}
    </div>
  );
}

function FindingCard({
  finding,
  theme,
  fonts,
  onJumpToField,
}: {
  finding: Finding;
  theme: Theme;
  fonts: FontPairing;
  onJumpToField: (id: string) => void;
}) {
  const kindColor: Record<string, string> = {
    contradiction: theme.confidenceLow,
    policy: theme.accent,
    missing: theme.inkSoft,
    quality: theme.inkSoft,
  };
  const kindLabel: Record<string, string> = {
    contradiction: "CONTRADICTION",
    policy: "POLICY",
    missing: "MISSING",
    quality: "QUALITY",
  };
  const color = kindColor[finding.kind] ?? theme.inkSoft;
  const label = kindLabel[finding.kind] ?? finding.kind.toUpperCase();

  return (
    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${theme.rule}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: "0.08em", color, fontWeight: 500 }}>{label}</span>
        <div style={{ flex: 1, height: 1, background: theme.rule }} />
      </div>
      <div style={{ fontFamily: fonts.sans, fontSize: 13.5, fontWeight: 500, color: theme.ink, letterSpacing: "-0.01em", marginBottom: 6, lineHeight: 1.35 }}>
        {finding.title}
      </div>
      <div style={{ fontFamily: fonts.sans, fontSize: 12.5, color: theme.inkSoft, lineHeight: 1.5 }}>{finding.body}</div>
      {finding.affects && finding.affects.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
          {finding.affects.map((fid) => (
            <button
              key={fid}
              onClick={() => onJumpToField(fid)}
              style={{
                fontFamily: fonts.mono,
                fontSize: 9,
                letterSpacing: "0.06em",
                padding: "3px 7px",
                background: theme.chipBg,
                border: `1px solid ${theme.chipBorder}`,
                color: theme.inkSoft,
                cursor: "pointer",
                textTransform: "uppercase",
              }}
            >
              → {fid.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightDerived({ intel, theme, fonts }: { intel: DocumentIntelligence; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ padding: "8px 0" }}>
      {intel.derived.map((d: DerivedField) => (
        <div key={d.id} style={{ padding: "14px 18px", borderBottom: `1px solid ${theme.rule}` }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkMute, letterSpacing: "0.06em" }}>
              {d.label.toUpperCase()}
            </span>
            <div style={{ flex: 1 }} />
            <span style={{ fontFamily: fonts.mono, fontSize: 9, color: theme.inkSoft, letterSpacing: "0.06em" }}>
              {d.flag === "ok" ? "✓ OK" : "CHECK"}
            </span>
          </div>
          <div style={{ fontFamily: fonts.sans, fontSize: 18, color: theme.ink, fontWeight: 500, letterSpacing: "-0.015em", marginBottom: 6 }}>
            {d.value}
          </div>
          <div style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.04em", marginBottom: 4 }}>
            {d.derivation}
          </div>
          <div style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkSoft, lineHeight: 1.45 }}>{d.note}</div>
        </div>
      ))}
    </div>
  );
}

function InsightActions({ intel, theme, fonts }: { intel: DocumentIntelligence; theme: Theme; fonts: FontPairing }) {
  return (
    <div style={{ padding: "14px 18px" }}>
      <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, lineHeight: 1.5, marginBottom: 14 }}>
        Suggested next steps based on the analysis. Nothing happens until you confirm.
      </div>
      {intel.actions.map((a: RecommendedAction, i) => (
        <div
          key={i}
          style={{ padding: "12px 14px", marginBottom: 8, border: `1px solid ${theme.rule}`, background: a.primary ? theme.panel : "transparent" }}
        >
          <div style={{ fontFamily: fonts.sans, fontSize: 13.5, fontWeight: 500, color: theme.ink, letterSpacing: "-0.005em", marginBottom: 4 }}>
            {a.label}
          </div>
          <div style={{ fontFamily: fonts.sans, fontSize: 11.5, color: theme.inkMute, lineHeight: 1.4 }}>{a.reason}</div>
          <button
            style={{
              marginTop: 10,
              fontFamily: fonts.sans,
              fontSize: 11.5,
              fontWeight: 500,
              padding: "5px 10px",
              background: a.primary ? theme.ink : "transparent",
              color: a.primary ? theme.docPaper : theme.ink,
              border: a.primary ? "none" : `1px solid ${theme.rule}`,
              cursor: "pointer",
            }}
          >
            {a.primary ? "Do it →" : "Run"}
          </button>
        </div>
      ))}
    </div>
  );
}
